import React, { useMemo } from "react";
import { Alert, Button, Card, Form, Input, Result, Skeleton, Typography } from "antd";

import type { WarrantyData } from "@/api/warranty";
import { daysDiffWarrantyEnd } from "@/utils";
import { buildTemplateFourTheme, splitCompanyPlaceholder } from "@/utils/template4Theme";
import WarrantySuccessIllustration from "./WarrantySuccessIllustration";
import WarrantyFooter from "./WarrantyFooter";
import ScratchCardRewardCard from "./ScratchCardRewardCard";
import "./baohanh-barcode.css";

export interface TemplateFourProps {
  loading: boolean;
  data: WarrantyData | null;
  notFound: boolean;
  activating: boolean;
  /** Chế độ xem trước (preview=1): hiển thị nhưng KHÔNG cho nhập/kích hoạt. */
  preview?: boolean;
  onActivate: (values: { name: string; phone: string }) => void;
  /** C14 — khách chọn nhà mạng nhận thẻ cào. */
  selectingTelecom?: boolean;
  onSelectTelecom?: (network: number) => void;
  /** D6 — khách nhập SĐT để nhận nạp tự động (topup). */
  onSetTopupPhone?: (phone: string) => void;
  /** Vừa nhận thưởng trong phiên này -> hiện khối trả thưởng (thay vì list). */
  justRewarded?: boolean;
}

/** Hàng thông tin trong bảng bảo hành — tự ẩn khi value rỗng. */
const InfoRow: React.FC<{ label: string; value?: React.ReactNode; alt?: boolean }> = ({ label, value, alt }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className={`${alt ? "gp-info-row--alt" : "bg-white"} px-3 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
      <dt className="gp-info-label text-sm font-medium">{label}</dt>
      <dd className="gp-info-value mt-1 text-sm sm:col-span-2 sm:mt-0">{value}</dd>
    </div>
  );
};

const formatVnd = (value?: number): string =>
  typeof value === "number" && value > 0 ? new Intl.NumberFormat("vi-VN").format(value) + " đ" : "";

/**
 * Trang kết quả bảo hành GoldenPen — dựng trên khung UI cũ baohanh-barcode nhưng
 * TẬN DỤNG dữ liệu + cấu hình hiển thị của hệ thống mới (scan-setting):
 *   - Logo / tên lấy từ khối `company` (fallback Canon); hotline là số hỗ trợ cố định.
 *   - Thông tin sản phẩm giàu (ảnh, mã, thương hiệu, danh mục, giá, mô tả, custom_fields).
 *   - Thông tin bảo hành (gói, dịch vụ Pisen Care+, ngày, số ngày còn lại) + xác thực.
 *   - Message theo trạng thái + cờ hiển thị (is_show_customer_info, is_show_product_price...).
 */
const TemplateFour: React.FC<TemplateFourProps> = ({
  loading,
  data,
  notFound,
  activating,
  preview = false,
  onActivate,
  selectingTelecom = false,
  onSelectTelecom,
  onSetTopupPhone,
  justRewarded = false,
}) => {
  const [form] = Form.useForm();

  const isError = notFound || !data || data.status === "not_found";

  // Trạng thái tem theo `is_activated`/`can_activate` (dựa trên status tem) — KHÔNG
  // dùng `is_client`: tem migrate có thể đã kích hoạt nhưng thiếu customer. Fallback
  // về hợp đồng legacy (is_client) khi BE chưa trả field mới.
  const hasNewState = !isError && data?.is_activated !== undefined;
  const isSuccess = !isError && (hasNewState ? !!data?.is_activated : !!data?.is_client);
  const canActivate = !isError && !isSuccess && (hasNewState ? !!data?.can_activate : true);
  const needActivate = canActivate;
  const isWarning = !isError && !isSuccess && !canActivate; // chưa xuất bán / khoá

  // Nạp tự động: khối "chúc mừng" chỉ dành cho lần kích hoạt trong phiên này
  // (justRewarded) hoặc khi khách còn phải thao tác (nhập SĐT) / có lỗi cần biết.
  // Mở lại trang (reload, justRewarded=false) -> chỉ xem trạng thái trả thưởng ở
  // list thông tin bên dưới, không chúc mừng lại.
  const reward = data?.scratch_card_reward;
  const isTopupReward = reward?.reward_type === "topup";
  const topupNeedsAction = !!reward?.needs_phone || !!reward?.error;
  const showRewardBlock =
    !!reward &&
    (isTopupReward
      ? justRewarded || topupNeedsAction
      : reward.status !== "issued" || justRewarded);
  // Trạng thái trả thưởng cho list (chỉ topup, khi không hiện khối phía trên).
  const showTopupPayoutInfo = !!reward && isTopupReward && !showRewardBlock;

  // --- Các khối dữ liệu (đều optional, có fallback) ---
  const company = data?.company;
  const product = data?.product;
  const warranty = data?.warranty;
  const verification = data?.verification;
  const config = data?.config;
  const customer = data?.customer;
  const message = data?.message;

  // Cấu hình hiển thị của template (màu + nội dung) — admin sửa ở màn "Mẫu trang xác thực".
  // Thiếu/không cấu hình thì `theme.get()` trả đúng giá trị vốn hardcode trước đây.
  const theme = useMemo(() => buildTemplateFourTheme(data?.template?.data), [data?.template?.data]);
  const [subtitleBefore, subtitleAfter] = splitCompanyPlaceholder(theme.get("success_subtitle"));

  const companyName = company?.name || "CANON";
  // Hotline lấy từ cấu hình template (như footer), KHÔNG lấy `company.phone`: đó là SĐT
  // doanh nghiệp (dữ liệu migrate có thể là số rác), không phải tổng đài hỗ trợ khách.
  const hotline = theme.get("hotline");

  const returnUrl = company?.website || "https://baohanh.goldenpen.com.vn";
  const noteContent = config?.landing_page_note_content;
  const activateNote = config?.landing_activate_note_content;

  const productImage = product?.image || data?.product_image || "";
  const gallery = (product?.images && product.images.length ? product.images : productImage ? [productImage] : []).filter(
    Boolean,
  );

  const remainingDays =
    typeof warranty?.warranty_days_left === "number"
      ? warranty.warranty_days_left
      : typeof data?.warranty_days_left === "number"
        ? data.warranty_days_left
        : daysDiffWarrantyEnd(warranty?.warranty_end || data?.warranty_end || "");

  // Nội dung cảnh báo: ưu tiên message cấu hình, fallback theo state.
  const warningTitle = message?.title || "CHƯA THỂ KÍCH HOẠT BẢO HÀNH";
  const warningContent =
    message?.content ||
    (data?.state === "locked"
      ? "Mã sản phẩm đã bị tạm khoá. Vui lòng liên hệ hotline để được hỗ trợ."
      : data?.state === "blocked"
        ? "Mã sản phẩm đã bị khoá. Vui lòng liên hệ hotline để được hỗ trợ."
        : "Sản phẩm có trên hệ thống nhưng chưa được xuất bán. Vui lòng liên hệ với nhân viên bán hàng để được hỗ trợ.");

  return (
    <div className="baohanh-barcode" style={theme.vars}>
      <div className="mx-auto max-w-7xl py-12 sm:px-6 lg:px-8 h-full">
        <div className="mx-auto max-w-4xl">
          <Card>
            {loading && (
              <div className="py-3 sm:px-6">
                <Skeleton active />
              </div>
            )}

            {!loading && (isSuccess || needActivate) && (
              <>
                <div className="pt-3 pb-3 sm:px-6">
                  <div className="text-center">
                    {isSuccess && (
                      <div>
                        <WarrantySuccessIllustration className="inline-block" />
                      </div>
                    )}
                    <h2 className="text-3xl">{theme.get("success_title")}</h2>
                    <div>
                      {subtitleBefore}
                      <span className="font-semibold">{companyName}</span>
                      {subtitleAfter}
                    </div>
                    {isSuccess && message?.content && (
                      <div className="gp-text-success mt-1">{message.content}</div>
                    )}
                    {needActivate && <div className="mb-4">{theme.get("activate_prompt")}</div>}
                  </div>

                  {needActivate && (
                    <div className="mt-3">
                      {preview && (
                        <Alert
                          type="info"
                          showIcon
                          className="mb-3"
                          message="Chế độ xem trước (preview)"
                          description="Đây là bản xem trước URL xác thực — không thể nhập thông tin hay kích hoạt bảo hành."
                        />
                      )}
                      <Form form={form} layout="vertical" onFinish={onActivate} disabled={preview}>
                        <Form.Item
                          label="Tên khách hàng"
                          name="name"
                          rules={[
                            { required: true, whitespace: true, message: "Vui lòng không được để trống" },
                            { min: 2, max: 250, message: "Độ dài giới hạn 2 đến 250 ký tự" },
                          ]}
                        >
                          <Input size="large" placeholder="Nhập tên khách hàng..." />
                        </Form.Item>
                        <Form.Item
                          label="Số điện thoại"
                          name="phone"
                          rules={[
                            { required: true, whitespace: true, message: "Vui lòng không được để trống" },
                            {
                              pattern: /(0[2|3|4|5|7|8|9])+([0-9]{8,9})\b/,
                              message: "Số điện thoại không đúng định dạng",
                            },
                            { min: 10, max: 10, message: "Độ dài giới hạn 10 ký tự" },
                          ]}
                        >
                          <Input size="large" placeholder="Nhập số điện thoại..." />
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            danger={!theme.hasCustomActivateButton}
                            htmlType="submit"
                            className={`w-full mt-3${theme.hasCustomActivateButton ? " gp-activate-btn" : ""}`}
                            size="large"
                            loading={activating}
                            disabled={preview}
                          >
                            {theme.get("activate_button_label")}
                          </Button>
                        </Form.Item>
                      </Form>

                      {/* Nội dung tuỳ chỉnh admin soạn — ngay dưới nút kích hoạt.
                          HTML đã được backend làm sạch bằng HTMLPurifier ở cả đường ghi lẫn đường đọc. */}
                      {activateNote && (
                        <div
                          className="gp-note gp-rich-text mt-3 text-sm"
                          dangerouslySetInnerHTML={{ __html: activateNote }}
                        />
                      )}
                    </div>
                  )}

                  {isSuccess && data && (
                    <div className="border-t border-gray-200 mt-5">
                      {gallery.length > 0 && (
                        <div className="flex justify-center gap-3 py-4 flex-wrap">
                          {gallery.slice(0, 3).map((src) => (
                            <img
                              key={src}
                              src={src}
                              alt={product?.name || data.product_name || "Sản phẩm"}
                              className="max-h-40 object-contain"
                            />
                          ))}
                        </div>
                      )}

                      {/* Thẻ cào (C12–C14) — khối trả thẻ đặt NGAY DƯỚI ảnh sản phẩm. Hiện khi:
                          - thẻ cào: CHƯA phát (chọn nhà mạng / đang xử lý / lỗi), HOẶC vừa
                            nhận trong phiên này (justRewarded) -> hiện voucher.
                          - nạp tự động: chỉ khi vừa kích hoạt trong phiên này, hoặc khách
                            còn phải nhập SĐT / có lỗi.
                          Khi mở lại trang (justRewarded=false), thông tin nằm trong list
                          thông tin serial bên dưới (C13). */}
                      {showRewardBlock && data.scratch_card_reward && (
                        <ScratchCardRewardCard
                          reward={data.scratch_card_reward}
                          selecting={selectingTelecom}
                          preview={preview}
                          justRewarded={justRewarded}
                          onSelectTelecom={(network) => onSelectTelecom?.(network)}
                          onSetPhone={(phone) => onSetTopupPhone?.(phone)}
                        />
                      )}
                      <dl>
                        {/* Thông tin khách hàng — chỉ khi admin bật is_show_customer_info */}
                        {customer && (
                          <>
                            <InfoRow label="Tên khách hàng" value={customer.name} alt />
                            <InfoRow label="Số điện thoại" value={customer.phone} />
                            <InfoRow label="Email" value={customer.email} alt />
                            <InfoRow label="Địa chỉ" value={customer.address} />
                          </>
                        )}

                        {/* Thông tin sản phẩm */}
                        <InfoRow label="Tên sản phẩm" value={product?.name || data.product_name} alt />
                        <InfoRow label="Mã sản phẩm" value={product?.item_code || data.product_code} />
                        <InfoRow label="Thương hiệu" value={product?.brand} alt />
                        <InfoRow label="Danh mục" value={product?.category} />
                        {config?.is_show_product_price && (
                          <InfoRow label="Giá niêm yết" value={formatVnd(product?.price)} alt />
                        )}
                        {product?.description && (
                          <InfoRow
                            label="Mô tả"
                            value={
                              <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: product.description }}
                              />
                            }
                          />
                        )}

                        {/* Custom fields của sản phẩm */}
                        {product?.custom_fields?.map((f, i) => (
                          <InfoRow
                            key={`cf-${i}`}
                            label={String(f.label || f.name || "Thông tin")}
                            value={f.value != null ? String(f.value) : ""}
                            alt={i % 2 === 0}
                          />
                        ))}

                        {/* Thông tin bảo hành */}
                        <InfoRow label="Gói bảo hành" value={warranty?.package_name} alt />
                        {warranty?.is_pisen_care_plus && (
                          <InfoRow label="Dịch vụ đi kèm" value={warranty?.service_name} />
                        )}
                        <InfoRow label="Thời gian bảo hành" value={`${warranty?.warranty_days ?? data.warranty_days ?? 0} ngày`} alt />
                        <InfoRow
                          label="Thời gian bắt đầu bảo hành"
                          value={`${warranty?.warranty_start || data.warranty_start || ""} - ${warranty?.warranty_end || data.warranty_end || ""}`}
                        />
                        <InfoRow label="Ngày kích hoạt" value={warranty?.activated_at || data.activated_at} alt />
                        <InfoRow
                          label="Số ngày bảo hành còn lại"
                          value={<span className="gp-text-success">{remainingDays} ngày</span>}
                        />
                        {/* S1 — tem cũ khách trả lại (đã thu hồi/thay thế). Giữ nền thành công,
                            chỉ đổi nhãn thành "Hàng bảo hành trả lại". */}
                        {warranty?.is_exchanged && (
                          <InfoRow
                            label="Trạng thái"
                            value={<span className="gp-text-warning">{theme.get("exchanged_status_label")}</span>}
                            alt
                          />
                        )}
                        {/* S2 — tem mới lấy ra thay thế, kế thừa bảo hành của tem cũ: "Hàng bảo hành đổi mới". */}
                        {warranty?.is_new_exchange && (
                          <InfoRow
                            label="Trạng thái"
                            value={<span className="gp-text-success">{theme.get("new_exchange_status_label")}</span>}
                            alt
                          />
                        )}

                        {/* Thông tin xác thực */}
                        {typeof verification?.scan_count === "number" && verification.scan_count > 0 && (
                          <InfoRow label="Số lần quét xác thực" value={`${verification.scan_count} lần`} />
                        )}

                        {/* Nạp tự động (C13) — mở lại trang: không chúc mừng nữa, chỉ cho biết
                            đã trả thưởng hay chưa. Không có serial/mã thẻ/nhà mạng như thẻ cào. */}
                        {showTopupPayoutInfo && data.scratch_card_reward && (
                          <>
                            <InfoRow
                              label="Trạng thái trả thưởng"
                              value={
                                data.scratch_card_reward.status === "issued"
                                  ? "Đã trả thưởng"
                                  : "Chưa trả thưởng"
                              }
                              alt
                            />
                            <InfoRow label="Mệnh giá thưởng" value={formatVnd(data.scratch_card_reward.card_amount)} />
                            <InfoRow
                              label="SĐT nhận thưởng"
                              value={data.scratch_card_reward.phone ?? undefined}
                              alt
                            />
                            {data.scratch_card_reward.status === "issued" && (
                              <InfoRow
                                label="Thời gian trả thưởng"
                                value={data.scratch_card_reward.issued_at ?? undefined}
                              />
                            )}
                            {/* Ghi chú khách hàng — help text dưới thông tin phần thưởng (lần xem lại). */}
                            {data.scratch_card_reward.message?.trim() && (
                              <div className="gp-info-row--alt px-3 py-3 sm:px-6">
                                <p className="gp-info-label m-0 whitespace-pre-line text-xs leading-relaxed">
                                  {data.scratch_card_reward.message.trim()}
                                </p>
                              </div>
                            )}
                          </>
                        )}

                        {/* Thẻ cào đã phát (C13) — chỉ vào list khi mở lại trang (không phải vừa
                            nhận trong phiên này); lần nhận đầu vẫn hiện ở khối trả thẻ phía trên. */}
                        {!isTopupReward && data.scratch_card_reward?.status === "issued" && !justRewarded && (
                          <>
                            <InfoRow label="Mệnh giá thẻ cào" value={formatVnd(data.scratch_card_reward.card_amount)} alt />
                            <InfoRow label="Nhà mạng thẻ cào" value={data.scratch_card_reward.network_name} />
                            <InfoRow
                              label="Serial thẻ cào"
                              value={
                                data.scratch_card_reward.card_serial ? (
                                  <Typography.Text copyable={{ text: data.scratch_card_reward.card_serial }} className="font-mono">
                                    {data.scratch_card_reward.card_serial}
                                  </Typography.Text>
                                ) : undefined
                              }
                              alt
                            />
                            <InfoRow
                              label="Mã thẻ cào"
                              value={
                                data.scratch_card_reward.card_code ? (
                                  <Typography.Text copyable={{ text: data.scratch_card_reward.card_code }} className="gp-card-code font-mono font-semibold">
                                    {data.scratch_card_reward.card_code}
                                  </Typography.Text>
                                ) : undefined
                              }
                            />
                            <InfoRow label="Hạn sử dụng thẻ cào" value={data.scratch_card_reward.card_expired_at ?? undefined} alt />
                            {/* Thông điệp campaign — help text ngay dưới thông tin mã thẻ (lần xem lại). */}
                            {data.scratch_card_reward.message?.trim() && (
                              <div className="bg-white px-3 py-3 sm:px-6">
                                <p className="gp-info-label m-0 whitespace-pre-line text-xs leading-relaxed">
                                  {data.scratch_card_reward.message.trim()}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </dl>

                      {noteContent && (
                        <div
                          className="gp-note gp-rich-text px-4 py-3 text-sm"
                          dangerouslySetInnerHTML={{ __html: noteContent }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {!loading && isWarning && (
              <div className="px-4 py-3 sm:px-6">
                <Result
                  status="warning"
                  title={warningTitle}
                  subTitle={
                    <>
                      <div>{warningContent}</div>
                      {(product?.name || data?.product_name) && (
                        <div>Sản phẩm: {product?.name || data?.product_name}</div>
                      )}
                      <div>Hotline hỗ trợ: {hotline}</div>
                    </>
                  }
                  extra={
                    <Button
                      type="primary"
                      danger={!theme.hasCustomErrorColor}
                      className={theme.hasCustomErrorColor ? "gp-danger-btn" : undefined}
                      href={returnUrl}
                    >
                      Trở về lại
                    </Button>
                  }
                />
              </div>
            )}

            {!loading && isError && (
              <div className="px-4 py-3 sm:px-6">
                <Result
                  status="500"
                  title="SẢN PHẨM CÓ DẤU HIỆU ĐÁNG NGỜ"
                  subTitle={
                    <>
                      <div>Vui lòng liên hệ với đại lý/ nhà phân phối gần nhất để biết thêm chi tiết.</div>
                      <div>Hotline hỗ trợ: {hotline}</div>
                    </>
                  }
                  extra={
                    <Button
                      type="primary"
                      danger={!theme.hasCustomErrorColor}
                      className={theme.hasCustomErrorColor ? "gp-danger-btn" : undefined}
                      href={returnUrl}
                    >
                      Trở về lại
                    </Button>
                  }
                />
              </div>
            )}

            <div className="border-t border-gray-200 mb-4 mt-2" style={{ minHeight: "50px" }}>
              <div className="gp-muted text-center px-2">
                {theme.get("thankyou_text")}{" "}
                <a className="gp-link" href={returnUrl}>
                  {companyName}!
                </a>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-3">
          <WarrantyFooter footerText={theme.get("footer_text")} hotline={theme.get("hotline")} />
        </div>
      </div>
    </div>
  );
};

export default TemplateFour;
